import { zodResolver } from '@hookform/resolvers/zod'
import * as React from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Form, FormControl, FormField, FormItem, FormMessage } from '../ui/form'
import { ApnSearchCounty } from './apn-search-county'
import { CustomLabelWithLock } from '../ui/custom-label-with-lock'
import { Button } from '../ui/button'
import { ApnSearchApn } from './apn-search-apn'
import { isEmpty } from 'lodash'

const formShema = z.object({
  apns: z.array(z.string()).min(1, 'Please select at least one APN'),
  county: z.string({
    required_error: 'Please select a county',
  }),
})

export type ApnSearchFormType = z.infer<typeof formShema>

interface Props {
  onSubmit: (values: ApnSearchFormType) => void
}

export default function ApnSearchForm({ onSubmit }: Props) {
  const form = useForm<ApnSearchFormType>({
    mode: 'onSubmit',
    resolver: zodResolver(formShema),
    defaultValues: {
      apns: [],
    },
  })

  const buttonRef = React.useRef<HTMLButtonElement>(null)
  const _onSubmit = (values: ApnSearchFormType) => onSubmit(values)

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(_onSubmit)}
        className="h-full space-y-2"
      >
        <FormField
          control={form.control}
          name="county"
          render={({ field }) => (
            <FormItem className="space-y-1 max-w-[560px] w-full">
              <CustomLabelWithLock title="County" hasAbility={true} />
              <FormControl>
                <ApnSearchCounty
                  selected={field.value}
                  setSelected={(v) => {
                    form.setValue('apns', [])
                    field.onChange(v)
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {form.watch('county') && (
          <FormField
            control={form.control}
            name="apns"
            render={({ field }) => (
              <FormItem className="space-y-1 max-w-[560px] w-full">
                <CustomLabelWithLock
                  title={`APN (${form.watch('county')})`}
                  hasAbility={true}
                />
                <FormControl>
                  <ApnSearchApn
                    disabled={isEmpty(form.watch('county'))}
                    hasAbility={true}
                    county={form.watch('county')}
                    selected={field.value}
                    setSelected={field.onChange}
                    onSearch={() => form.handleSubmit(_onSubmit)()}
                    onTab={() => buttonRef?.current?.focus()}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        {form.watch('county') && (
          <div className="flex flex-col justify-end flex-1">
            <div className="mt-2">
              <Button
                ref={buttonRef}
                className="inline-flex items-center font-bold justify-center gap-2 whitespace-nowrap transition-colors
    focus:outline-none focus:ring-2 focus:ring-black focus:border-black
    disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0
    bg-primary text-primary-foreground hover:opacity-90 h-10 rounded-md px-4
    disabled:bg-[#808080] disabled:opacity-100 disabled:cursor-not-allowed w-full"
                size={'md'}
                type="submit"
                disabled={!form.formState.isValid}
              >
                <p>Search</p>
              </Button>
            </div>
          </div>
        )}
      </form>
    </Form>
  )
}
